<template>
  <div class="flex flex-col gap-4">
    <div v-if="isRunning" class="glass-tile flex flex-col items-center gap-2 rounded-xl p-5 text-center text-muted-foreground">
      <span class="flex items-center gap-2 text-base font-semibold text-foreground">
        <loader-circle-icon class="size-5 animate-spin text-primary" />
        {{ tr('Your first ads are being prepared...') }}
      </span>
      {{ tr('Hold on, this can take a few minutes. New activities will appear here automatically.') }}
    </div>

    <ol v-if="!isListEmpty || hasMore || (isRunning && isListEmpty) || skeleton" class="m-0 flex list-none flex-col p-0">
      <li v-for="(event, index) in logs" :key="index" class="flex flex-col">
        <h4 v-if="showDate(index)" :class="[ 'm-0 pb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase', index === 0 ? '' : 'pt-3' ]">
          {{ formattedDate(event.created_at) }}
        </h4>

        <div class="flex gap-3">
          <div class="flex w-14 shrink-0 flex-col items-center">
            <span :class="[ 'mt-3 flex size-7 shrink-0 items-center justify-center rounded-full text-white', actionStyle(event).bg ]">
              <component :is="actionIcon(event)" class="size-3.5" />
            </span>
            <span class="mt-1 text-xs whitespace-nowrap text-muted-foreground tabular-nums">{{ formatTime(event.created_at) }}</span>
            <span v-if="index < logs.length - 1 || hasMore" class="mt-1 w-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            data-slot="activity-event"
            :class="[ 'glass-tile mb-3 flex min-w-0 flex-1 cursor-pointer flex-col overflow-hidden rounded-xl text-left', GLOW_TRANSITION, actionStyle(event).glow ]"
            @click="() => openActivityDetails(event)"
          >
            <!-- AI-made ads: the header fades into purple towards the right. -->
            <span
              :class="[
                'flex min-w-0 items-center gap-2 px-3 py-1.5 text-xs font-semibold text-white',
                actionStyle(event).bg,
                event.created_ad?.created_with_ai && 'bg-[linear-gradient(to_right,transparent_10%,#9800bc_100%)]',
              ]"
            >
              <circle-check-icon v-if="event.action !== 'error'" class="size-3.5 shrink-0" />
              <span class="truncate">
                {{ actionText(event) }}<template v-if="getAdFromEvent(event)?.variation"> - v{{ getAdFromEvent(event).variation }}</template>
              </span>
              <span v-if="!ruleId && event.rule" class="ml-auto truncate font-normal opacity-90">{{ event.rule.name }}</span>
              <ai-created-badge
                v-if="event.created_ad?.created_with_ai"
                :class="!ruleId && event.rule ? '' : 'ml-auto'"
                :show-label="!mobile && !$root.isMobile"
              />
            </span>
            <span class="flex items-center gap-3 p-3">
              <inline-social-content
                v-if="event.post || getAdFromEvent(event)"
                class="min-w-0 flex-1"
                :entity="event.post"
                :text="getText(event)"
                :badge="event.affected_ad_count > 1 ? tr('Total :count ad variants', { ':count': event.affected_ad_count }) : null"
              />
              <span v-else-if="getText(event)" class="min-w-0 flex-1 text-sm text-foreground">{{ getText(event) }}</span>
              <chevron-right-icon class="size-5 shrink-0 text-primary" />
            </span>
          </button>
        </div>
      </li>

      <template v-if="hasMore || isRunning || skeleton">
        <li
          v-for="i in 3"
          :key="'activity-log-loader-skeleton--' + i"
          ref="scrollPagerTrigger"
          :class="[ 'flex flex-col', isRunning ? 'opacity-60' : '' ]"
        >
          <ui-skeleton v-if="i === 1 && isListEmpty" class="mb-2 h-3.5 w-24" />
          <div class="flex gap-3">
            <div class="flex w-14 shrink-0 flex-col items-center gap-1 pt-3">
              <ui-skeleton class="size-7 rounded-full" />
              <ui-skeleton class="h-3 w-9" />
            </div>
            <div class="glass-tile mb-3 flex min-w-0 flex-1 flex-col gap-3 rounded-xl p-3">
              <ui-skeleton class="h-3 w-40" />
              <inline-social-content />
            </div>
          </div>
        </li>
      </template>
    </ol>

    <empty-screen
      v-else
      class="my-5"
      icon="rocket"
      :title="tr('Your automation history will appear here')"
      :flag-text="tr('Monitoring active')"
      :description="tr('When your automation starts working, you\'ll see a chronological list of all activities here, including started and stopped ads, policy checks, and other automated actions.')"
      :mobile="mobile"
    />
  </div>
</template>

<script>
import { ChevronRight, CircleCheck, LoaderCircle, Pause, Play, TrendingUp, TriangleAlert } from '@lucide/vue';
import { markRaw } from 'vue';

import { RULE_ACTION } from '@/consts.js';
import RuleActivityLog from '@/entities/RuleActivityLog.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import BrandService from '@/services/Brand.js';
import DashboardService from '@/services/Dashboard.js';
import RuleService from '@/services/Rule.js';
import ActivityDetailsModal from '@/ui/components/ActivityDetailsModal.vue';
import AiCreatedBadge from '@/ui/components/AiCreatedBadge.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import { GLOW_TRANSITION, ruleActionStyle } from '@/ui/utils/ruleAction.js';
import { formatTime, formatToStringMD } from '@/utils/date.js';
import debounce from '@/utils/debounce.js';

const DESTRUCTIVE_STYLE = {
  bg: 'bg-destructive',
  glow: 'hover:[--tile-glow:color-mix(in_oklab,var(--destructive)_40%,transparent)]',
};

const ITEM_PER_PAGE = 10;
const REFRESH_POLL_INTERVAL_MS = 5000;

export default {
  name: 'ActivityLog',
  components: {
    AiCreatedBadge,
    ChevronRightIcon: ChevronRight,
    CircleCheckIcon: CircleCheck,
    EmptyScreen,
    InlineSocialContent,
    LoaderCircleIcon: LoaderCircle,
  },
  mixins: [ ScrollPagerMixin ],
  props: {
    ruleId: {
      type: [ String, null ],
      default: null,
    },
    brandId: {
      type: [ String, Number, null ],
      default: null,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    isRunning: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      GLOW_TRANSITION,
      isLoading: false,
      hasMore: true,
      page: 0,
      logs: [],
      refreshPollingIntervalId: null,
      debouncedWatchListener: debounce(() => {
        this.destroyScrollPagerObserver();
        this.reset();
        this.loadNewData();
      }, 100),
    };
  },
  computed: {
    isListEmpty() {
      return this.logs.length === 0;
    },
  },
  watch: {
    ruleId() {
      this.debouncedWatchListener();
    },
    brandId() {
      this.debouncedWatchListener();
    },
    isRunning: {
      immediate: true,
      handler(val) {
        if (val) {
          this.startRefreshPolling();
        }
        else {
          this.stopRefreshPolling();
        }
      },
    },
  },
  mounted() {
    this.debouncedWatchListener();
  },
  beforeUnmount() {
    this.stopRefreshPolling();
  },
  methods: {
    formatTime,
    loadNewData(resetBeforeAppend = false) {
      if (this.isLoading || (!this.isRunning && !this.hasMore) || this.skeleton) {
        return;
      }

      this.isLoading = true;
      ++this.page;

      let serviceCall;

      if (this.ruleId) {
        const params = {
          id: this.ruleId,
          page: this.page,
          limit: ITEM_PER_PAGE,
        };
        serviceCall = RuleService.getLogs(params);
      }
      else if (this.brandId) {
        serviceCall = BrandService.getActivityLogs({
          id: this.brandId,
          page: this.page,
          limit: ITEM_PER_PAGE,
        });
      }
      else {
        serviceCall = DashboardService.getActivityLogs(null, null, this.page, ITEM_PER_PAGE);
      }

      serviceCall
        .then(resp => {
          if (resetBeforeAppend) {
            this.reset();
          }

          resp.forEach(item => this.logs.push(new RuleActivityLog(item)));
          this.hasMore = resp.length === ITEM_PER_PAGE;
          return resp;
        })
        .catch(err => {
          this.hasMore = false;
          this.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          if (this.hasMore && !this.isRunning) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
          }
        });
    },
    reset() {
      this.logs = [];
      this.page = 0;
      this.hasMore = true;
    },
    refresh() {
      if (this.isLoading || this.skeleton) {
        return;
      }

      this.loadNewData(true);
    },
    startRefreshPolling() {
      if (this.refreshPollingIntervalId) {
        return;
      }

      this.refreshPollingIntervalId = setInterval(() => {
        if (!this.isRunning) {
          this.stopRefreshPolling();
          return;
        }

        this.refresh();
      }, REFRESH_POLL_INTERVAL_MS);
    },
    stopRefreshPolling() {
      if (!this.refreshPollingIntervalId) {
        return;
      }

      clearInterval(this.refreshPollingIntervalId);
      this.refreshPollingIntervalId = null;
    },
    getAdFromEvent(event) {
      if (event.created_ad) {
        return event.created_ad;
      }

      return event.ad || null;
    },
    openActivityDetails(event) {
      this.$fullscreenModal.open(
        this,
        ActivityDetailsModal,
        { event },
      );
    },
    getText(event) {
      const ad = this.getAdFromEvent(event);
      if (ad && ad.text) {
        return ad.text;
      }
      if (ad) {
        return ad.name;
      }
      if (event.post) {
        return event.post.text;
      }
      if (event.text) {
        return event.text;
      }
      return null;
    },
    actionStyle(event) {
      if (event.action === RULE_ACTION.DELETE || event.action === 'error') {
        return DESTRUCTIVE_STYLE;
      }

      return ruleActionStyle(event.action);
    },
    actionIcon(event) {
      switch (event.action) {
        case RULE_ACTION.START:
          return markRaw(Play);

        case RULE_ACTION.SCALING_UP:
          return markRaw(TrendingUp);

        case 'error':
          return markRaw(TriangleAlert);

        default:
          return markRaw(Pause);
      }
    },
    actionText(event) {
      switch (event.action) {
        case RULE_ACTION.START:
        case RULE_ACTION.SCALING_UP:
          return event.created_ad?.created_with_ai && (this.mobile || this.$root.isMobile)
            ? this.tr('New ad created with AI')
            : this.tr('New ad was created');

        case RULE_ACTION.STOP:
        case 'smart_stop':
          return this.tr('Your ad was stopped');

        case RULE_ACTION.DELETE:
          return this.tr('An ad was deleted');

        case 'error':
          return this.tr('Could not create an ad from this content.');
      }

      return '';
    },
    formattedDate(date) {
      return formatToStringMD(date, this.tr);
    },
    showDate(index) {
      if (index === 0) {
        return true;
      }
      if (index > 0) {
        const currentDate = this.formattedDate(this.logs[index].created_at);
        const previousDate = this.formattedDate(this.logs[index - 1].created_at);
        return currentDate !== previousDate;
      }
      return true;
    },
  },
};
</script>
